import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmtNum, timeAgo } from "@/api";
import { Rows, ArrowsInSimple } from "@phosphor-icons/react";

export function FVGTable({ pairs }) {
  const [timeframe, setTimeframe] = useState("5m");
  const [selectedSymbol, setSelectedSymbol] = useState(pairs?.[0]?.symbol || "BTCUSDT");
  const [onlyUnfilled, setOnlyUnfilled] = useState(true);

  useEffect(() => {
    if (!selectedSymbol && pairs?.length) setSelectedSymbol(pairs[0].symbol);
  }, [pairs, selectedSymbol]);

  const { data, isLoading } = useQuery({
    queryKey: ["fvg", selectedSymbol],
    queryFn: () => api.fvg(selectedSymbol),
    enabled: !!selectedSymbol,
    refetchInterval: 30_000,
  });

  const rows = (timeframe === "5m" ? data?.fvg_5m : data?.fvg_1m) || [];
  const filtered = onlyUnfilled ? rows.filter((r) => !r.filled) : rows;

  return (
    <div data-testid="fvg-panel" className="border border-white/10 bg-[#0A0A0A] h-full flex flex-col">
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3 bg-[#0f0f0f] flex-wrap">
        <ArrowsInSimple size={16} className="text-[#007AFF]" />
        <h2 className="font-display font-bold text-sm tracking-widest uppercase text-white">Fair Value Gaps</h2>

        <div className="relative ml-2">
          <input
            data-testid="fvg-symbol-search"
            list="fvg-symbol-list"
            value={selectedSymbol || ""}
            onChange={(e) => {
              const v = e.target.value.toUpperCase();
              setSelectedSymbol(v);
            }}
            placeholder="Type or pick pair"
            className="bg-black border border-white/10 rounded-none px-2 py-1 text-white font-mono text-[11px] w-40"
            title={`${(pairs || []).length} pairs available — start typing to filter`}
          />
          <datalist id="fvg-symbol-list">
            {(pairs || []).map((p) => (
              <option key={p.symbol} value={p.symbol}>
                {p.symbol}
              </option>
            ))}
          </datalist>
        </div>

        <div className="flex border border-white/10 rounded-none" data-testid="fvg-tf-toggle">
          {["1m", "5m"].map((tf) => (
            <button
              key={tf}
              data-testid={`fvg-tf-${tf}`}
              onClick={() => setTimeframe(tf)}
              className={`px-3 py-1 font-mono text-[11px] uppercase tracking-widest ${
                timeframe === tf ? "bg-[#00FF66] text-black" : "text-white/60 hover:bg-white/5"
              }`}
            >
              {tf}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-2 font-mono text-[11px] text-white/60 ml-auto cursor-pointer">
          <input
            type="checkbox"
            data-testid="fvg-unfilled-only"
            checked={onlyUnfilled}
            onChange={(e) => setOnlyUnfilled(e.target.checked)}
            className="accent-[#00FF66]"
          />
          Unfilled only
        </label>

        {data && (
          <span className="font-mono text-[11px] text-white/40">
            Price {fmtNum(data.current_price)}
          </span>
        )}
      </div>

      <div className="flex-1 overflow-auto">
        <table className="w-full text-left" data-testid="fvg-table">
          <thead className="text-[10px] font-mono uppercase text-white/50 sticky top-0 bg-[#0f0f0f] border-b border-white/10">
            <tr>
              <th className="px-3 py-2 tracking-widest">Time (UTC)</th>
              <th className="px-3 py-2 tracking-widest text-right">Low</th>
              <th className="px-3 py-2 tracking-widest text-right">High</th>
              <th className="px-3 py-2 tracking-widest text-right">Size %</th>
              <th className="px-3 py-2 tracking-widest text-right">Age</th>
              <th className="px-3 py-2 tracking-widest">Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center font-mono text-xs text-white/40">
                  Loading FVGs<span className="terminal-cycle">...</span>
                </td>
              </tr>
            )}
            {!isLoading && filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center font-mono text-xs text-white/40">
                  No {onlyUnfilled ? "unfilled " : ""}gaps on {timeframe}. Market is efficient right now.
                </td>
              </tr>
            )}
            {filtered.map((r, i) => (
              <tr
                key={i}
                data-testid={`fvg-row-${i}`}
                className="border-b border-white/5 hover:bg-white/[0.02]"
              >
                <td className="px-3 py-2 font-mono text-[11px] text-white/70">
                  {new Date(r.time).toISOString().replace("T", " ").slice(0, 16)}
                </td>
                <td className="px-3 py-2 font-mono text-[12px] text-right text-white">{fmtNum(r.low)}</td>
                <td className="px-3 py-2 font-mono text-[12px] text-right text-white">{fmtNum(r.high)}</td>
                <td className="px-3 py-2 font-mono text-[12px] text-right text-[#F5C518]">{r.size_pct.toFixed(3)}%</td>
                <td className="px-3 py-2 font-mono text-[11px] text-right text-white/60">{r.age_bars}b</td>
                <td className="px-3 py-2">
                  {r.filled ? (
                    <span className="font-mono text-[10px] px-2 py-[2px] border border-white/10 text-white/40 uppercase tracking-widest">
                      Filled
                    </span>
                  ) : (
                    <span className="font-mono text-[10px] px-2 py-[2px] border border-[#00FF66]/30 text-[#00FF66] uppercase tracking-widest bg-[#00FF66]/5">
                      Unfilled
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
